/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useMemo, useState } from 'react'
import { defineMessages, FormattedMessage } from 'react-intl'
import moment from 'moment'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiFlyoutHeader,
  EuiFlyout,
  EuiFlyoutBody,
  EuiTitle,
  EuiFlyoutFooter,
  EuiFormRow,
} from '@elastic/eui'

import {
  invalidateQueriesAfterTrialExtension,
  useExtendTrialAdminV1,
} from '@modules/billing-lib/hooks'
import { CuiAlert } from '@modules/cui/Alert'
import { addToast } from '@modules/cui/Toasts'

import SpinButton from '@/components/SpinButton'

import type { Props } from './types'

const messages = defineMessages({
  extendTrial: {
    id: `extend-trial.extend-trial`,
    defaultMessage: `Extend trial`,
  },
  extendedTrial: {
    id: `extend-trial.updated-trial`,
    defaultMessage: `Granted trial extension`,
  },
  cancel: {
    id: `extend-trial.cancel`,
    defaultMessage: `Cancel`,
  },
  save: {
    id: `extend-trial.save`,
    defaultMessage: `Extend trial`,
  },
  generalError: {
    id: `extend-trial.error`,
    defaultMessage: `Something went wrong`,
  },
})

const ExtendTrial = ({ currentTrial, organizationId, fetchSaasOrganization }: Props) => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState<boolean>(false)
  const [selectedExpiration, setSelectedExpiration] = useState<string>(currentTrial.end)
  const { mutate, isError, isLoading, error } = useExtendTrialAdminV1()

  const closeFlyout = () => {
    setIsFlyoutOpen(false)
  }

  const openFlyout = () => {
    setIsFlyoutOpen(true)
  }

  const onTrialExtended = () => {
    // for Redux flow
    fetchSaasOrganization(organizationId)
    // for RQ flow
    invalidateQueriesAfterTrialExtension()
    closeFlyout()
    addToast({
      color: `success`,
      title: <FormattedMessage {...messages.extendedTrial} />,
    })
  }

  const isSelectedDateValid = useMemo(
    () => moment(selectedExpiration).endOf('day').isAfter(moment(currentTrial.start)),
    [selectedExpiration, currentTrial],
  )

  const extendTrial = () => {
    mutate(
      { organizationId, expiration: moment(selectedExpiration).endOf('day').utc().format() },
      { onSuccess: onTrialExtended },
    )
  }

  const renderExtendTrialFlyout = () => {
    if (!isFlyoutOpen) {
      return null
    }

    return (
      <EuiFlyout
        ownFocus={true}
        size='s'
        aria-labelledby='extendTrialFlyoutTitle'
        onClose={closeFlyout}
      >
        <EuiFlyoutHeader hasBorder={true}>
          <EuiTitle size='s'>
            <h2 id='extendTrialFlyoutTitle'>
              <FormattedMessage id='extend-trial.flyout-title' defaultMessage='Extend trial' />
            </h2>
          </EuiTitle>
        </EuiFlyoutHeader>

        <EuiFlyoutBody>
          <EuiFormRow
            isInvalid={!isSelectedDateValid}
            error={
              !isSelectedDateValid ? (
                <FormattedMessage
                  id='extend-trial.expiration-date.error'
                  defaultMessage='Expiration date must be on or after trial start date'
                />
              ) : undefined
            }
            fullWidth={true}
            label={
              <FormattedMessage
                id='extend-trial.expiration-date-label'
                defaultMessage='Expiration date'
              />
            }
          >
            <EuiFieldText
              type='date'
              value={moment(selectedExpiration).format(`YYYY-MM-DD`)}
              onChange={(e) => setSelectedExpiration((e.target as HTMLInputElement).value)}
              readOnly={isLoading}
              isInvalid={!isSelectedDateValid}
              data-test-id='extend-trial-date-picker'
            />
          </EuiFormRow>

          {isError && (
            <Fragment>
              <EuiSpacer />
              <CuiAlert type='error'>
                {error || <FormattedMessage {...messages.generalError} />}
              </CuiAlert>
            </Fragment>
          )}
        </EuiFlyoutBody>

        <EuiFlyoutFooter>
          <EuiFlexGroup gutterSize='m' alignItems='center' justifyContent='spaceBetween'>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty disabled={isLoading} onClick={closeFlyout}>
                <FormattedMessage {...messages.cancel} />
              </EuiButtonEmpty>
            </EuiFlexItem>

            <EuiFlexItem grow={false}>
              <SpinButton
                fill={true}
                spin={isLoading}
                onClick={extendTrial}
                disabled={!isSelectedDateValid}
                data-test-id='extend-trial-button'
              >
                <FormattedMessage {...messages.save} />
              </SpinButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlyoutFooter>
      </EuiFlyout>
    )
  }

  return (
    <Fragment>
      <EuiButton size='s' onClick={openFlyout} data-test-id='open-extend-trial-flyout'>
        <FormattedMessage {...messages.extendTrial} />
      </EuiButton>

      {renderExtendTrialFlyout()}
    </Fragment>
  )
}

export default ExtendTrial
